import { Reducer } from 'redux';
import {
    API_KEYS_ERROR,
    API_KEYS_CLEAR_ERROR,
    API_KEYS_CLEAR_SUCCESS,
    API_KEYS_RESET,
    API_KEYS_FETCH_START,
    API_KEYS_FETCH_SUCCESS,
    API_KEYS_SAVE_START,
    API_KEYS_SAVE_SUCCESS,
    API_KEYS_ROTATE_START,
    API_KEYS_ROTATE_SUCCESS,
    API_KEYS_REVOKE_START,
    API_KEYS_REVOKE_SUCCESS,
    ApiKeysAction
} from 'actions/apiKeys/ApiKeysActionsTypes';
import { ApiKeysState, ApiKeysStatus } from './ApiKeysState';

const initialState: ApiKeysState = {
    apiKeys: [],
    status: ApiKeysStatus.Initial,
    loading: false
};

/**
 * Replaces a key in the list, or prepends it when it is new.
 */
const upsert = (apiKeys: ApiKeysState['apiKeys'], apiKey: ApiKeysState['apiKeys'][number]) => {
    const index = apiKeys.findIndex(existing => existing.id === apiKey.id);
    if (index === -1) {
        return [apiKey, ...apiKeys];
    }

    const next = [...apiKeys];
    next[index] = apiKey;
    return next;
};

export const apiKeysReducer: Reducer<ApiKeysState, ApiKeysAction> = (state = initialState, action) => {
    switch (action.type) {
        case API_KEYS_RESET:
            return {
                ...initialState
            };

        case API_KEYS_ERROR:
            return {
                ...state,
                loading: false,
                status: ApiKeysStatus.Error,
                error: action.payload.error
            };

        case API_KEYS_CLEAR_ERROR:
            return {
                ...state,
                status: ApiKeysStatus.Initial,
                error: undefined
            };

        case API_KEYS_CLEAR_SUCCESS:
            return {
                ...state,
                status: ApiKeysStatus.Initial,
                savedApiKey: undefined
            };

        case API_KEYS_FETCH_START:
            return {
                ...state,
                loading: true,
                status: ApiKeysStatus.Fetching,
                error: undefined
            };

        case API_KEYS_FETCH_SUCCESS:
            return {
                ...state,
                loading: false,
                status: ApiKeysStatus.FetchSuccess,
                apiKeys: action.payload.apiKeys
            };

        case API_KEYS_SAVE_START:
            return {
                ...state,
                loading: true,
                status: ApiKeysStatus.Saving,
                error: undefined
            };

        case API_KEYS_SAVE_SUCCESS:
            return {
                ...state,
                loading: false,
                status: ApiKeysStatus.SaveSuccess,
                savedApiKey: action.payload.apiKey,
                apiKeys: upsert(state.apiKeys, action.payload.apiKey)
            };

        case API_KEYS_ROTATE_START:
            return {
                ...state,
                loading: true,
                status: ApiKeysStatus.Rotating,
                error: undefined
            };

        case API_KEYS_ROTATE_SUCCESS:
            return {
                ...state,
                loading: false,
                status: ApiKeysStatus.RotateSuccess,
                savedApiKey: action.payload.apiKey,
                apiKeys: upsert(state.apiKeys, action.payload.apiKey)
            };

        case API_KEYS_REVOKE_START:
            return {
                ...state,
                loading: true,
                status: ApiKeysStatus.Revoking,
                error: undefined
            };

        // Revocation is logical: the key stays in the list, marked inactive,
        // and its client_id is no longer usable.
        case API_KEYS_REVOKE_SUCCESS:
            return {
                ...state,
                loading: false,
                status: ApiKeysStatus.RevokeSuccess,
                apiKeys: state.apiKeys.map(apiKey =>
                    apiKey.id === action.payload.apiKeyId ? { ...apiKey, active: false } : apiKey
                )
            };

        default:
            return state;
    }
};
